import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { usePhases, useItemsForProject, useProjects } from "@/hooks/useData";
import { TaskPanel } from "@/components/TaskPanel";
import { useLocation } from "react-router-dom";
import { itemProjectId } from "@/components/ItemRow";
import { QuickAdd, type QuickAddTarget } from "@/components/QuickAdd";
import type { Item } from "@/lib/types";

interface EditorValue {
  openItem: (item: Item) => void;
  openNew: (projectId?: string, phaseId?: string, due?: string) => void;
  /** Quick Add from the header or Q: into the project whose page you're on, else the Inbox. */
  quickAddHere: () => void;
}

const TaskEditorContext = createContext<EditorValue | null>(null);

export function TaskEditorProvider({ children }: { children: ReactNode }) {
  const [item, setItem] = useState<Item | null>(null);
  const [quickAdd, setQuickAdd] = useState<QuickAddTarget | null>(null);
  const { pathname } = useLocation();

  const openItem = useCallback((it: Item) => setItem(it), []);

  /**
   * "+ Add" opens Quick Add, defaulting to the project (and section) the
   * button belongs to and, from a calendar day, that date. The task is created
   * in Todoist and arrives here on the sync that follows.
   */
  const openNew = useCallback((projectId?: string, phaseId?: string, due?: string) => {
    setQuickAdd({ projectId, phaseId, due });
  }, []);

  const quickAddHere = useCallback(() => {
    setQuickAdd((cur) => cur ?? { projectId: /^\/project\/([^/]+)/.exec(pathname)?.[1] });
  }, [pathname]);

  // Q opens it from anywhere — unless you are typing somewhere, or holding a
  // modifier. On a project's page it defaults to that project.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "q" && e.key !== "Q") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName))) return;
      e.preventDefault();
      quickAddHere();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [quickAddHere]);

  return (
    <TaskEditorContext.Provider value={{ openItem, openNew, quickAddHere }}>
      {children}
      {item && (
        <EditorPanel item={item} onOpenItem={openItem} onClose={() => setItem(null)} />
      )}
      {quickAdd && <QuickAdd target={quickAdd} onClose={() => setQuickAdd(null)} />}
    </TaskEditorContext.Provider>
  );
}

function EditorPanel({ item, onOpenItem, onClose }: {
  item: Item; onOpenItem: (i: Item) => void; onClose: () => void;
}) {
  const scope = itemProjectId(item);
  const { data: projects = [] } = useProjects();
  const { data: phases = [] } = usePhases(scope);
  const { data: siblings = [] } = useItemsForProject(scope);

  return (
    <TaskPanel
      key={item.id}
      item={item}
      project={projects.find((p) => p.id === scope)}
      phase={phases.find((ph) => ph.id === item.phase)}
      siblings={siblings}
      onOpenItem={onOpenItem}
      onClose={onClose}
    />
  );
}

export function useTaskEditor(): EditorValue {
  const ctx = useContext(TaskEditorContext);
  if (!ctx) throw new Error("useTaskEditor must be used within TaskEditorProvider");
  return ctx;
}
